import React, { useState, useEffect } from "react";
import { BarChart3 } from "lucide-react";
import { usePageConfig } from "../../contexts/PageConfigContext";
import { ordersApi, productionApi } from "../../services/api";
import { Order, ProductionRecord, ProductSummary } from "../../types";
import { KPICards } from "./components/KPICards";
import { ProductionComparisonChart } from "./components/ProductionComparisonChart";
import { OrderStatusChart } from "./components/OrderStatusChart";
import { TopProductsChart } from "./components/TopProductsChart";

type DatePreset = "today" | "week" | "month";

export const DashboardPage: React.FC = () => {
  const { setPageConfig } = usePageConfig();
  const [selectedPreset, setSelectedPreset] = useState<DatePreset>("today");
  const [orders, setOrders] = useState<Order[]>([]);
  const [productionRecords, setProductionRecords] = useState<
    ProductionRecord[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setPageConfig({
      title: "Dashboard",
      description: "Análisis y métricas de producción",
      actions: [],
    });
  }, [setPageConfig]);

  const getDateRange = (preset: DatePreset): { from: string; to: string } => {
    const today = new Date();

    switch (preset) {
      case "today":
        return {
          from: today.toISOString().split("T")[0],
          to: today.toISOString().split("T")[0],
        };
      case "week":
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay() + 1);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return {
          from: weekStart.toISOString().split("T")[0],
          to: weekEnd.toISOString().split("T")[0],
        };
      case "month":
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          from: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`,
          to: monthEnd.toISOString().split("T")[0],
        };
    }
  };

  useEffect(() => {
    const abortController = new AbortController();

    const loadData = async () => {
      try {
        setIsLoading(true);
        const range = getDateRange(selectedPreset);

        const [ordersData, productionData] = await Promise.all([
          ordersApi.getAll({ dateFrom: range.from, dateTo: range.to }),
          productionApi.getRecords({ from: range.from, to: range.to }),
        ]);

        // Only update state if not aborted
        if (!abortController.signal.aborted) {
          setOrders(ordersData as any);
          setProductionRecords(productionData as any);
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error("Error loading dashboard data:", err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      abortController.abort();
    };
  }, [selectedPreset]);

  // Calculate KPIs
  const totalOrders = orders.length;
  const completedOrders = orders.filter((o) => o.status === "completed").length;
  const pendingOrders = orders.filter((o) => o.status === "pending").length;
  const completionRate =
    totalOrders > 0 ? (completedOrders / totalOrders) * 100 : 0;

  // Calculate production summary
  const productionSummary: ProductSummary[] = productionRecords.reduce(
    (acc, record) => {
      const existing = acc.find((item) => item.productId === record.productId);
      if (existing) {
        const unitEntry = existing.quantities.find(
          (q) => q.unit === record.unit,
        );
        if (unitEntry) {
          unitEntry.quantity += record.quantity;
        } else {
          existing.quantities.push({
            quantity: record.quantity,
            unit: record.unit,
          });
        }
      } else {
        acc.push({
          productId: record.productId,
          productName: record.productName,
          quantities: [{ quantity: record.quantity, unit: record.unit }],
          recordCount: 1,
        });
      }
      return acc;
    },
    [] as ProductSummary[],
  );

  // Calculate orders summary
  const ordersSummary: ProductSummary[] = orders.reduce((acc, order) => {
    order.lines.forEach((line) => {
      const existing = acc.find((item) => item.productId === line.productId);
      if (existing) {
        const unitEntry = existing.quantities.find((q) => q.unit === line.unit);
        if (unitEntry) {
          unitEntry.quantity += line.quantity;
        } else {
          existing.quantities.push({
            quantity: line.quantity,
            unit: line.unit,
          });
        }
      } else {
        acc.push({
          productId: line.productId,
          productName: line.productName,
          quantities: [{ quantity: line.quantity, unit: line.unit }],
          recordCount: 1,
        });
      }
    });
    return acc;
  }, [] as ProductSummary[]);

  // Prepare data for production comparison chart
  const comparisonData = [
    ...new Set([
      ...productionSummary.map((p) => p.productId),
      ...ordersSummary.map((o) => o.productId),
    ]),
  ]
    .map((productId) => {
      const prod = productionSummary.find((p) => p.productId === productId);
      const order = ordersSummary.find((o) => o.productId === productId);
      return {
        productName: prod?.productName || order?.productName || "",
        production:
          prod?.quantities.reduce((sum, q) => sum + q.quantity, 0) || 0,
        orders: order?.quantities.reduce((sum, q) => sum + q.quantity, 0) || 0,
      };
    })
    .sort((a, b) => b.production + b.orders - (a.production + a.orders))
    .slice(0, 10);

  // Prepare data for order status chart
  const statusData = [
    {
      name: "Pendientes",
      value: orders.filter((o) => o.status === "pending").length,
      color: "#eab308",
    },
    {
      name: "En Proceso",
      value: orders.filter((o) => o.status === "processing").length,
      color: "#3b82f6",
    },
    {
      name: "Completadas",
      value: orders.filter((o) => o.status === "completed").length,
      color: "#10b981",
    },
  ].filter((d) => d.value > 0);

  // Prepare data for top products chart
  const topProductsData = ordersSummary
    .map((p) => ({
      productName: p.productName,
      quantity: p.quantities.reduce((sum, q) => sum + q.quantity, 0),
    }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 10);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-4"></div>
          <p className="text-slate-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-16">
      {/* Date Range Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="text-green-600" size={20} />
          <h3 className="text-lg font-bold text-slate-900">
            Período de Análisis
          </h3>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedPreset("today")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPreset === "today"
                ? "bg-green-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Hoy
          </button>
          <button
            onClick={() => setSelectedPreset("week")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPreset === "week"
                ? "bg-green-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Esta Semana
          </button>
          <button
            onClick={() => setSelectedPreset("month")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              selectedPreset === "month"
                ? "bg-green-600 text-white"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            }`}
          >
            Este Mes
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <KPICards
        totalOrders={totalOrders}
        completedOrders={completedOrders}
        pendingOrders={pendingOrders}
        completionRate={completionRate}
        productionMatchRate={0}
      />

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:pointer-events-auto pointer-events-none">
        <ProductionComparisonChart data={comparisonData} />
        <OrderStatusChart data={statusData} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:pointer-events-auto pointer-events-none">
        <TopProductsChart data={topProductsData} />
      </div>
    </div>
  );
};
